// Visual study only. No imports from combat, no attack or save state mutations.
import type { AWOKEN_PROXY_POSES } from './awoken_king_proxy'
import { exactCube, exactPow08 } from './exact_pow'

export type HuntPose = (typeof AWOKEN_PROXY_POSES)[string]
export type WallSide = 'left' | 'right'
export type HuntWallLife = 'none' | 'weak' | 'strong'
export type HuntWallHeight = 'current' | 'middle' | 'high'
// The proxy body is roughly 80px tall at this scale. The high stop leaves
// clearance for the head below the decorative arch at the wall.
export const HUNT_HEIGHT_LIFT: Record<HuntWallHeight, number> = { current: 0, middle: 24, high: 70 }
export const HUNT_FLOOR = 440
export const HUNT_SCALE = 0.32
export type HuntPathSize = 'real' | 'proxy'
// The proxy's idle joints span about 320 source px; at 0.58 they occupy the
// same ~186 screen px as idle.webp at the game's fixed display scale.
export const HUNT_REAL_PROXY_SCALE = 0.58
export const HUNT_DURATION = 4400
export const HUNT_WALLS = { left: 60, right: 900 }
// DEV art only. The game uses displayHeight=120*1.55 and fixed refH=850
// (render.ts / sprites.ts), with no flat-pose correction for these sources.
export const HUNT_REAL_ART_SCALE = 120 * 1.55 / 850
export type HuntRealRole = 'idle' | 'coil' | 'strike' | 'catch' | 'wall' | 'settle'
export type HuntArtChoice = 'A' | 'B'
type ArtSource = { file: string; width: number; height: number; bounds: readonly [number, number, number, number]; sizeFix?: number; body?: readonly [number, number]; feet?: Partial<Record<'nf' | 'ff' | 'nh' | 'fh', readonly [number, number]>> }
// Alpha > 16 bounds measured from the existing WebP sprites. Coordinates are
// source pixels; bbox bottom supports floor poses, rear/right edge supports wall.
export const HUNT_REAL_ART: Record<HuntRealRole, { A: ArtSource; B?: ArtSource }> = {
  idle: { A: { file: 'idle.webp', width: 1441, height: 852, bounds: [1, 1, 1440, 851] } },
  coil: { A: { file: 'dash_crouch_fangs_0912.webp', width: 1361, height: 438, bounds: [0, 0, 1361, 438], sizeFix: 1.23 }, B: { file: 'chase_coil_fangs_0924.webp', width: 1444, height: 695, bounds: [1, 1, 1444, 695], sizeFix: 1.23 } },
  strike: { A: { file: 'swoop_bite_0920.webp', width: 1926, height: 756, bounds: [32, 18, 1919, 734], sizeFix: 0.89 }, B: { file: 'rush_lunge_fangs_0917.webp', width: 1374, height: 549, bounds: [0, 0, 1374, 549], sizeFix: 1.08 } },
  catch: { A: { file: 'slam_landing_0920.webp', width: 1537, height: 1011, bounds: [12, 3, 1533, 1006], sizeFix: 0.87 }, B: { file: 'rush_overextend_fangs_0912.webp', width: 1412, height: 718, bounds: [0, 0, 1412, 718], sizeFix: 1.09 } },
  wall: { A: { file: 'wall_hunt_cling_0925.webp', width: 1264, height: 1199, bounds: [8, 8, 1258, 1191], sizeFix: 0.90, body: [830, 615], feet: { nf: [1252, 835], ff: [1252, 1009], nh: [1252, 201], fh: [1252, 482] } }, B: { file: 'wall_kick_fangs_0912.webp', width: 973, height: 924, bounds: [0, 0, 973, 924], sizeFix: 1.10 } },
  settle: { A: { file: 'rampage_slam_fangs_1007.webp', width: 1249, height: 614, bounds: [0, 1, 1249, 611], sizeFix: 1.12 }, B: { file: 'bite_recover_rest_0917.webp', width: 1594, height: 986, bounds: [51, 142, 1563, 884], sizeFix: 1.01 } },
}
// New study sprites share the idle body mass at source resolution. Coordinates
// describe actual claws/soles and the torso centre, never the alpha rectangle.
export const HUNT_NEW_ART: Record<HuntRealRole | 'kick', ArtSource> = {
  idle: HUNT_REAL_ART.idle.A,
  coil: { file: 'wall_hunt_coil_0924.webp', width: 1468, height: 860, bounds: [0, 0, 1468, 860], body: [740, 403], feet: { nf: [84, 826], ff: [432, 836], nh: [757, 825], fh: [1216, 836] } },
  strike: { file: 'wall_hunt_strike_0924.webp', width: 1792, height: 856, bounds: [0, 0, 1792, 856], body: [845, 431] },
  catch: { file: 'wall_hunt_catch_0924.webp', width: 1477, height: 873, bounds: [0, 0, 1477, 873], body: [730, 390], feet: { nf: [77, 838], ff: [319, 841] } },
  wall: { file: 'wall_hunt_wall_0924.webp', width: 1084, height: 1298, bounds: [0, 0, 1084, 1298], body: [640, 600], feet: { nf: [1057, 1204], ff: [1057, 950], nh: [1057, 205], fh: [1057, 500] } },
  settle: { file: 'wall_hunt_settle_0924.webp', width: 1439, height: 852, bounds: [0, 0, 1439, 852], body: [705, 385], feet: { nf: [72, 820], ff: [328, 819], nh: [894, 807], fh: [1269, 819] } },
  kick: { file: 'wall_hunt_kick_0924.webp', width: 1545, height: 915, bounds: [0, 0, 1545, 915], body: [758, 467] },
}
export const HUNT_OLD_KICK_ART: ArtSource = { file: 'wall_hunt_kick_0925.webp', width: 1622, height: 928, bounds: [8, 8, 1614, 920], sizeFix: 1.0, body: [798, 451], feet: { nh: [1515, 354], fh: [1453, 474], nf: [57, 863], ff: [439, 860] } }
export const HUNT_FANGFIX_ART = {
  strike: { ...HUNT_REAL_ART.strike.A, file: 'swoop_bite_fangfix_0925.webp' },
  catch: { ...HUNT_REAL_ART.catch.A, file: 'slam_landing_fangfix_0925.webp' },
  kick: { ...HUNT_OLD_KICK_ART, file: 'wall_hunt_kick_fangfix_0925.webp' },
} as const
export type HuntFangFix = 'head8' | 'fixed' | 'original'
// Measured transplant bounds/hulls; keep the original torso pivot and claw coordinates.
export const HUNT_HEAD8_ART = {
  strike: { ...HUNT_REAL_ART.strike.A, file: 'swoop_bite_head8_0925.webp', body: [975.5, 376], bounds: [57, 18, 1919, 734] },
  catch: { ...HUNT_REAL_ART.catch.A, file: 'slam_landing_head8_0925.webp', bounds: [12, 3, 1533, 1006] },
  kick: { ...HUNT_OLD_KICK_ART, file: 'wall_hunt_kick_head8_0925.webp', bounds: [8, 8, 1614, 920] },
} as const
// Convex outlines of source pixels with alpha > 16. Generated from the original WebP files;
// all transformed extrema are on these points, including at arbitrary rotation.
const HUNT_ALPHA_HULLS: Record<string, string> = {
  'swoop_bite_head8_0925.webp': '1918,299 1910,502 1905,538 1904,540 1897,547 1801,632 1798,634 1790,638 1787,639 1783,640 811,733 803,733 798,732 254,600 244,590 239,584 88,362 86,359 58,303 57,300 57,297 107,173 108,172 159,141 586,18 587,18 891,37 1858,218 1862,219 1865,220 1869,222 1890,237 1894,240 1906,252 1907,254 1917,288 1918,294',
  'slam_landing_head8_0925.webp': '1532,196 1531,207 1472,547 1453,588 1450,591 1414,611 1412,612 587,971 581,973 453,1005 451,1005 100,978 52,973 17,963 16,962 12,956 12,944 31,673 67,552 118,397 139,353 248,247 362,151 520,77 1017,3 1031,3 1473,94 1477,95 1503,113 1507,116 1510,119 1524,144 1528,152 1529,155 1532,192',
  'wall_hunt_kick_head8_0925.webp': '1613,319 1612,323 1601,357 1496,463 1494,465 1489,469 1479,474 490,919 25,907 22,905 19,902 16,898 14,895 11,888 10,885 9,881 8,870 8,869 9,861 62,531 94,412 96,410 138,377 395,203 535,129 1523,8 1538,8 1545,9 1550,10 1559,12 1586,39 1590,44 1593,48 1595,51 1598,56 1604,68 1608,98',

  'wall_hunt_takeoff_0925.webp': '1599,654 1573,918 1572,920 1566,926 1562,928 1559,929 1511,941 1458,941 1262,940 1211,937 1177,934 1174,933 294,540 292,539 256,507 251,502 226,469 223,463 157,292 156,289 155,284 155,282 156,277 157,274 162,264 297,52 298,51 359,19 401,6 578,29 644,43 790,121 1542,562 1547,565 1568,584 1577,593 1587,609 1590,614 1591,616 1596,628 1597,631 1598,636 1599,644',
  'wall_hunt_cling_0925.webp': '8,869 59,754 61,750 62,749 844,127 895,95 905,89 923,80 1215,8 1217,8 1239,26 1248,69 1249,74 1257,254 1257,259 1256,500 1253,864 1250,912 1245,981 1244,988 1240,1010 1236,1018 1230,1024 1226,1027 1220,1030 861,1181 857,1182 812,1189 805,1190 772,1190 251,1152 239,1146 206,1127 204,1125 187,1106 74,973 36,926 18,892 11,878 9,873 8,870',
  'wall_hunt_kick_0925.webp': '8,869 9,861 72,392 395,203 535,129 1523,8 1538,8 1545,9 1550,10 1559,12 1586,39 1590,44 1593,48 1595,51 1598,56 1604,68 1608,98 1613,319 1612,323 1601,357 1496,463 1494,465 1489,469 1479,474 490,919 25,907 22,905 19,902 16,898 14,895 11,888 10,885 9,881 8,870',
  'bite_recover_rest_0917.webp': '51,476 56,444 65,422 71,410 74,405 79,397 81,394 88,386 151,326 155,323 318,241 397,202 630,142 1143,260 1301,321 1306,323 1313,326 1325,332 1336,338 1346,344 1366,359 1375,367 1386,378 1398,392 1401,396 1409,407 1411,410 1417,420 1421,427 1557,683 1558,685 1560,690 1562,698 1562,702 1559,738 1558,742 1555,753 1554,756 1550,764 1529,782 1370,865 1368,866 1365,867 1331,876 1275,877 588,883 586,883 234,861 206,853 198,849 85,599 56,527 55,524 52,509 51,494',
  'chase_coil_fangs_0924.webp': '1,659 9,113 10,109 87,21 93,15 149,1 389,31 472,55 1394,329 1397,330 1401,332 1422,349 1428,355 1431,359 1433,362 1436,368 1437,371 1442,387 1443,392 1443,409 1442,413 1441,416 1360,658 1357,664 1344,686 1301,694 237,694 199,692 30,676 8,670 2,667 1,665',
  'dash_crouch_fangs_0912.webp': '0,173 36,91 39,85 40,84 83,59 297,12 380,0 381,0 525,8 904,74 1020,98 1032,101 1042,104 1057,110 1278,208 1305,223 1308,225 1323,243 1327,248 1329,251 1331,255 1334,272 1335,278 1360,437 0,437',
  'idle.webp': '1,305 34,190 35,189 265,29 403,1 585,28 691,64 1079,206 1087,209 1211,256 1221,260 1239,269 1249,275 1258,281 1262,284 1271,291 1277,296 1294,313 1303,324 1306,328 1314,340 1319,348 1331,372 1335,381 1338,389 1436,657 1438,664 1439,669 1439,671 1435,708 1434,713 1433,717 1430,725 1425,735 1410,751 1406,755 1403,757 1393,763 1391,764 1262,824 1259,825 1228,835 475,850 474,850 209,828 184,821 176,817 175,816 1,307',
  'rampage_slam_fangs_0917.webp': '0,563 23,360 36,250 37,249 219,82 230,72 315,16 449,1 451,1 917,13 925,14 947,22 955,25 1039,57 1044,59 1056,64 1071,72 1076,75 1084,80 1088,83 1097,90 1103,95 1122,114 1126,119 1133,128 1135,131 1140,139 1143,144 1147,151 1153,163 1157,172 1162,185 1245,413 1246,416 1247,420 1248,425 1248,428 1245,454 1244,461 1243,465 1240,474 1237,480 1222,498 1216,503 1212,506 1206,510 1089,571 1087,572 1059,580 326,610 33,580 9,573 1,569 0,567',
  // 2026-10-19: 壁跳ねの停止の絵も 10-07 に描き直した17番へ (ユーザー「17-12」。口の外は 0917 と同じ寸法・同じ alpha なので輪郭も同じ)。
  'rampage_slam_fangs_1007.webp': '0,563 23,360 36,250 37,249 219,82 230,72 315,16 449,1 451,1 917,13 925,14 947,22 955,25 1039,57 1044,59 1056,64 1071,72 1076,75 1084,80 1088,83 1097,90 1103,95 1122,114 1126,119 1133,128 1135,131 1140,139 1143,144 1147,151 1153,163 1157,172 1162,185 1245,413 1246,416 1247,420 1248,425 1248,428 1245,454 1244,461 1243,465 1240,474 1237,480 1222,498 1216,503 1212,506 1206,510 1089,571 1087,572 1059,580 326,610 33,580 9,573 1,569 0,567',
  'rush_lunge_fangs_0917.webp': '0,471 70,181 109,99 110,97 113,93 114,92 150,77 330,12 446,4 1197,0 1228,0 1234,1 1238,2 1245,4 1251,7 1269,21 1272,24 1273,26 1282,47 1285,56 1368,375 1373,398 1373,410 1359,427 1353,433 1345,437 1244,481 1238,483 216,548 0,548',
  'rush_overextend_fangs_0912.webp': '0,661 6,652 197,392 380,201 382,199 475,120 575,68 894,9 966,0 1011,0 1027,5 1042,10 1047,12 1059,18 1066,22 1071,25 1080,31 1354,229 1373,247 1386,260 1389,264 1399,294 1402,305 1403,311 1411,717 0,717',
  'slam_landing_0920.webp': '12,944 17,616 51,497 52,495 139,353 248,247 362,151 520,77 1017,3 1031,3 1473,94 1477,95 1503,113 1507,116 1510,119 1524,144 1528,152 1529,155 1532,192 1532,196 1531,207 1472,547 1453,588 1450,591 1414,611 1412,612 587,971 581,973 453,1005 451,1005 100,978 52,973 17,963 16,962 12,956',
  'swoop_bite_0920.webp': '32,315 81,164 586,18 587,18 891,37 1858,218 1862,219 1865,220 1869,222 1890,237 1894,240 1906,252 1907,254 1917,288 1918,294 1918,299 1910,502 1905,538 1904,540 1897,547 1801,632 1798,634 1790,638 1787,639 1783,640 811,733 803,733 798,732 254,601 248,598 245,596 223,577 221,575 193,540 72,381 51,353 45,344 41,337 34,323 32,317',
  'wall_hunt_catch_0924.webp': '10,248 51,147 53,143 54,142 93,105 242,34 363,11 370,10 374,10 544,49 1161,192 1165,193 1178,197 1196,203 1208,208 1219,213 1225,216 1238,223 1252,232 1258,236 1270,245 1275,249 1281,254 1291,263 1293,265 1306,279 1311,285 1317,293 1467,511 1473,523 1474,526 1475,530 1475,565 1474,575 1473,580 1472,584 1471,587 1469,592 1468,594 1452,616 1443,625 1437,629 1301,718 1270,726 1096,753 367,857 365,857 313,853 91,826 60,821 42,815 41,814 40,810 10,253',
  'wall_hunt_coil_0924.webp': '20,353 21,351 62,291 66,287 250,112 410,75 528,76 603,98 1044,316 1058,323 1163,379 1174,385 1190,394 1214,408 1233,420 1244,427 1258,437 1262,440 1267,444 1273,449 1288,463 1293,468 1307,484 1457,672 1459,675 1464,685 1465,688 1466,692 1467,699 1467,709 1462,735 1461,740 1458,749 1453,759 1441,772 1432,781 1429,783 1417,789 1414,790 1266,827 1216,836 1215,836 431,835 84,827 44,818 42,816 41,813 20,371',
  'wall_hunt_kick_0924.webp': '13,850 22,393 57,274 58,271 59,269 116,229 339,76 497,64 513,63 1396,9 1412,9 1420,10 1431,13 1434,14 1462,30 1467,33 1475,39 1481,44 1504,77 1506,80 1510,88 1512,93 1514,100 1515,104 1516,109 1517,116 1535,446 1535,448 1518,484 1447,605 1422,632 1393,640 360,901 358,901 29,891 22,884 20,881 17,875 15,869 14,864 13,857',
  'wall_hunt_settle_0924.webp': '1,302 30,196 32,192 254,33 388,2 390,2 565,22 674,71 1209,343 1222,350 1234,357 1240,361 1250,368 1262,377 1274,387 1291,404 1299,413 1309,426 1320,443 1427,616 1430,621 1434,629 1436,635 1437,659 1437,678 1436,683 1432,694 1428,704 1427,706 1421,715 1417,719 1316,815 1314,816 1276,830 444,831 442,831 100,811 74,805 66,800 65,799 1,304',
  'wall_hunt_strike_0924.webp': '22,496 23,492 24,489 148,226 192,140 193,139 238,116 432,34 564,20 565,20 762,61 1687,277 1691,278 1712,284 1714,285 1731,296 1737,300 1742,304 1744,306 1751,314 1754,318 1759,328 1764,358 1765,368 1770,627 1770,630 1762,679 1760,681 1741,698 1739,699 1589,761 1586,762 1582,763 1571,763 256,686 211,658 88,569 41,531 22,499',
  'wall_hunt_wall_0924.webp': '20,925 31,830 216,351 220,343 222,340 225,336 247,311 250,308 255,305 595,114 611,106 616,104 634,98 642,96 652,94 660,93 670,92 685,92 696,93 705,94 711,95 722,97 727,98 739,101 749,104 755,106 1042,215 1046,217 1054,225 1057,230 1058,232 1063,258 1063,1219 1053,1241 1050,1244 1046,1247 1040,1250 1035,1252 1030,1253 1005,1253 260,1151 225,1144 217,1140 211,1136 192,1122 190,1120 46,969 35,953 33,950 27,940 21,928',
  'wall_kick_fangs_0912.webp': '0,923 1,307 35,205 36,203 37,202 77,174 293,24 415,0 421,0 590,6 696,48 940,193 958,210 960,212 969,251 972,403 972,404 969,654 968,715 964,761 963,768 962,772 959,781 956,786 950,792 782,898 777,901 775,902 772,903 728,909 715,910',
}
const hullCache = new Map<string, readonly (readonly [number, number])[]>()
function huntArtHull(art: ArtSource): readonly (readonly [number, number])[] {
  let hull = hullCache.get(art.file)
  if (!hull) {
    const sourceFile = art.file.replace('_fangfix_0925.webp', art.file.startsWith('wall_hunt_kick') ? '_0925.webp' : '_0920.webp')
    hull = (HUNT_ALPHA_HULLS[art.file] ?? HUNT_ALPHA_HULLS[sourceFile]).split(' ').map(pair => pair.split(',').map(Number) as [number, number])
    hullCache.set(art.file, hull)
  }
  return hull
}

export type HuntArtMode = 'new' | 'old' | 'proxy'
export type HuntKickMode = 'own' | 'tilt'
export type HuntTakeoffMode = 'A' | 'B'
export const HUNT_TAKEOFF_ART: ArtSource = { file: 'wall_hunt_takeoff_0925.webp', width: 1657, height: 949, bounds: [155, 6, 1600, 942], sizeFix: 1.10, body: [850, 500], feet: { nh: [1500, 938], fh: [1215, 938] } }
export function huntStudyArt(frame: HuntFrame, mode: HuntArtMode, kick: HuntKickMode, choices: Partial<Record<HuntRealRole, HuntArtChoice>> = {}, fangFix: HuntFangFix = 'original'): ArtSource | undefined {
  if (mode === 'proxy') return undefined
  const corrected = fangFix === 'head8' ? HUNT_HEAD8_ART : fangFix === 'fixed' ? HUNT_FANGFIX_ART : undefined
  if (frame.key?.image === 'takeoff') return HUNT_TAKEOFF_ART
  if (mode === 'old' && frame.key?.ms === 2440 && kick === 'own') return corrected ? corrected.kick : HUNT_OLD_KICK_ART
  if (mode === 'old') {
    const role = huntRealRole(frame.key?.image ?? '')
    if (corrected && role && (role === 'strike' || role === 'catch') && (choices[role] ?? 'A') === 'A') return corrected[role]
    return huntRealArt(frame.key?.image ?? '', choices)
  }
  if (frame.key?.ms === 2440 && kick === 'own') return HUNT_NEW_ART.kick
  return HUNT_NEW_ART[frame.key?.image as HuntRealRole] ?? huntRealArt(frame.key?.image ?? '', choices)
}
export const huntRealRole = (image: string): HuntRealRole | undefined =>
  Object.prototype.hasOwnProperty.call(HUNT_REAL_ART, image) ? image as HuntRealRole : undefined
export function huntRealArt(image: string, choices: Partial<Record<HuntRealRole, HuntArtChoice>> = {}): ArtSource | undefined {
  const role = huntRealRole(image)
  if (!role) return undefined
  return HUNT_REAL_ART[role][choices[role] ?? 'A'] ?? HUNT_REAL_ART[role].A
}
export type HuntRealPlacement = { x: number; y: number; pivotX: number; pivotY: number; scaleX: number; scaleY: number; rotation: number }
export function huntRealArtPoint(frame: HuntFrame, placement: HuntRealPlacement, point: readonly [number, number]): [number, number] {
  const dx = (point[0] - placement.pivotX) * placement.scaleX, dy = (point[1] - placement.pivotY) * placement.scaleY
  return [placement.x + (dx * Math.cos(placement.rotation) - dy * Math.sin(placement.rotation)) * frame.facing,
    placement.y + dx * Math.sin(placement.rotation) + dy * Math.cos(placement.rotation)]
}
// Rotation is about the visible body's bbox centre, not the canvas corner.
// Keep the study's root and motion channels; translate the art to its contact.
export function huntRealPlacement(frame: HuntFrame, side: WallSide, art: ArtSource, size: 'fix' | 'raw' = 'raw'): HuntRealPlacement {
  const [x0, y0, x1, y1] = art.bounds
  const pivotX = art.body?.[0] ?? (x0 + x1) / 2, pivotY = art.body?.[1] ?? (y0 + y1) / 2
  const motion = frame.motion
  const corrected = size === 'fix' ? art.sizeFix ?? 1 : 1
  let scaleX = HUNT_REAL_ART_SCALE * corrected * (motion?.scale.x ?? 1)
  let scaleY = HUNT_REAL_ART_SCALE * corrected * (motion?.scale.y ?? 1)
  const rotation = ((art === HUNT_NEW_ART.kick || art === HUNT_OLD_KICK_ART || art === HUNT_FANGFIX_ART.kick || art === HUNT_HEAD8_ART.kick) && frame.key?.ms === 2440 ? -8
    : art === HUNT_REAL_ART.wall.A ? 0 : art === HUNT_NEW_ART.wall ? (motion?.rot ?? 0) * 0.15 : motion?.rot ?? 0) * Math.PI / 180
  let x = frame.x + (motion?.offset.x ?? 0), y = frame.y + (motion?.offset.y ?? 0)
  const point = (px: number, py: number): [number, number] => {
    const dx = (px - pivotX) * scaleX, dy = (py - pivotY) * scaleY
    return [x + (dx * Math.cos(rotation) - dy * Math.sin(rotation)) * frame.facing,
      y + dx * Math.sin(rotation) + dy * Math.cos(rotation)]
  }
  if (frame.key?.wall.length) {
    // Source art looks left: its rightmost rear sole becomes the wall-side edge.
    const foot = art.feet?.[frame.key.wall[0]]
    x += HUNT_WALLS[side] - point(foot?.[0] ?? x1, foot?.[1] ?? pivotY)[0]
  }
  if (frame.key?.floor.length) {
    const foot = art.feet?.[frame.key.floor[0]]
    y += HUNT_FLOOR - point(foot?.[0] ?? pivotX, foot?.[1] ?? y1)[1]
  }
  const current = (): HuntRealPlacement => ({ x, y, pivotX, pivotY, scaleX, scaleY, rotation })
  if (frame.pathSize === 'real') {
    // Keep declared contacts within 3px while fitting the transformed alpha
    // hull. A wall/floor anchor is allowed to sit just inside its plane when
    // the painted outline extends beyond the recorded claw point.
    const wallFoot = frame.key?.wall[0] && art.feet?.[frame.key.wall[0]]
    const floorFoot = frame.key?.floor[0] && art.feet?.[frame.key.floor[0]]
    const wallSource: readonly [number, number] | undefined = frame.key?.wall.length ? wallFoot ?? [x1, pivotY] : undefined
    const floorSource: readonly [number, number] | undefined = frame.key?.floor.length ? floorFoot ?? [pivotX, y1] : undefined
    const wallInset = art === HUNT_REAL_ART.wall.A ? 1.5 : 2.99
    const wallTarget = side === 'left' ? HUNT_WALLS.left + wallInset : HUNT_WALLS.right - wallInset
    const floorTarget = HUNT_FLOOR - 2.99
    // Re-anchor after resizing: the actual rendered claw, not the root,
    // remains the contact point.
    const anchor = () => {
      if (wallSource) x += wallTarget - huntRealArtPoint(frame, current(), wallSource)[0]
      if (floorSource) y += floorTarget - huntRealArtPoint(frame, current(), floorSource)[1]
    }
    anchor()
    for (let i = 0; i < 3; i++) {
      let [left, top, right, bottom] = huntRealArtBounds(frame, current(), art)
      // Translation keeps the intended sprite size whenever that axis has no
      // planted contact. Resize only if translation cannot solve the overlap.
      if (!wallSource) x += Math.max(0, HUNT_WALLS.left - left) - Math.max(0, right - HUNT_WALLS.right)
      if (!floorSource) y += Math.max(0, 8 - top) - Math.max(0, bottom - HUNT_FLOOR)
      ;[left, top, right, bottom] = huntRealArtBounds(frame, current(), art)
      let factor = 1
      const horizontal = wallSource ? wallTarget : (left + right) / 2
      const vertical = floorSource ? floorTarget : (top + bottom) / 2
      if (left < HUNT_WALLS.left) factor = Math.min(factor, (horizontal - HUNT_WALLS.left) / (horizontal - left))
      if (right > HUNT_WALLS.right) factor = Math.min(factor, (HUNT_WALLS.right - horizontal) / (right - horizontal))
      if (top < 8) factor = Math.min(factor, (vertical - 8) / (vertical - top))
      if (bottom > HUNT_FLOOR) factor = Math.min(factor, (HUNT_FLOOR - vertical) / (bottom - vertical))
      if (factor < 1) {
        const safe = Math.max(0.01, factor)
        scaleX *= safe; scaleY *= safe
        x = horizontal + (x - horizontal) * safe
        y = vertical + (y - vertical) * safe
        anchor()
      }
    }
  }
  return { x, y, pivotX, pivotY, scaleX, scaleY, rotation }
}
export function huntRealContact(frame: HuntFrame, placement: HuntRealPlacement, art: ArtSource): { floorY: number; wallX: number } {
  const [, , x1, y1] = art.bounds
  const floor = art.feet?.[frame.key?.floor[0] ?? 'nf']
  const wall = art.feet?.[frame.key?.wall[0] ?? 'nh']
  const point = (px: number, py: number): [number, number] => huntRealArtPoint(frame, placement, [px, py])
  return {
    floorY: point(floor?.[0] ?? placement.pivotX, floor?.[1] ?? y1)[1],
    wallX: point(wall?.[0] ?? x1, wall?.[1] ?? placement.pivotY)[0],
  }
}
const base: HuntPose = {
  shoulder: [330, 430], hip: [620, 415], head: [205, 415],
  nf: [300, 720], ff: [368, 720], nh: [600, 720], fh: [668, 720], jaw: 0.45,
}
const pose = (overrides: Partial<HuntPose>): HuntPose => ({ ...base, ...overrides })
const poses = {
  idle: base,
  coil: pose({ shoulder: [350, 545], hip: [635, 510], head: [218, 540],
    nf: [285, 720], ff: [365, 720], nh: [575, 720], fh: [650, 720],
    frontBend: -70, hindBend: 95, jaw: 0.7, arch: -12 }),
  push: pose({ shoulder: [320, 475], hip: [595, 540], head: [175, 463],
    nf: [280, 580], ff: [365, 580], nh: [735, 720], fh: [780, 720], jaw: 0.7 }),
  tuck: pose({ shoulder: [330, 475], hip: [615, 450], head: [188, 476],
    nf: [400, 565], ff: [445, 540], nh: [550, 580], fh: [615, 550],
    frontBend: -65, hindBend: 75, jaw: 0.75 }),
  strike: pose({ shoulder: [310, 480], hip: [595, 445], head: [150, 480],
    nf: [180, 590], ff: [365, 580], nh: [540, 590], fh: [615, 560],
    frontBend: -30, hindBend: 70, jaw: 0.95 }),
  catch: pose({ shoulder: [320, 515], hip: [605, 445], head: [175, 500],
    nf: [210, 720], ff: [310, 720], nh: [565, 605], fh: [630, 570],
    frontBend: -20, hindBend: 65, jaw: 0.55 }),
  gather: pose({ shoulder: [360, 510], hip: [645, 490], head: [245, 480],
    nf: [250, 720], ff: [330, 720], nh: [550, 720], fh: [630, 720],
    frontBend: -55, hindBend: 95, jaw: 0.65 }),
  reach: pose({ shoulder: [380, 435], hip: [585, 635], head: [260, 370],
    nf: [183, 430], ff: [165, 515], nh: [650, 760], fh: [700, 720],
    headAngle: 0.45, jaw: 0.7, frontBend: -15, hindBend: 45 }),
  turn: pose({ shoulder: [478, 405], hip: [510, 690], head: [475, 305],
    nf: [160, 480], ff: [142, 540], nh: [545, 690], fh: [585, 700],
    headAngle: 0.6, jaw: 0.65, frontBend: -35, hindBend: 65 }),
  wall: pose({ shoulder: [490, 435], hip: [550, 715], head: [345, 400],
    nf: [310, 590], ff: [390, 545], nh: [860, 640], fh: [842, 730],
    frontBend: -60, hindBend: 100, jaw: 0.85, headAngle: -0.08 }),
  wallPush: pose({ shoulder: [330, 500], hip: [615, 450], head: [188, 495],
    nf: [345, 590], ff: [420, 565], nh: [950, 640], fh: [932, 730],
    frontBend: -60, hindBend: 15, jaw: 0.9 }),
  settle: pose({ shoulder: [340, 490], hip: [625, 465], head: [210, 465],
    nf: [260, 720], ff: [335, 720], nh: [640, 720], fh: [710, 720],
    frontBend: -25, hindBend: 55, jaw: 0.6 }),
}

type Key = { ms: number; x: number; y: number; pose: keyof typeof poses; facing: 1 | -1 }
// First pass goes left; the second pass returns right. Mirror the entire study for the other wall.
const keys: Key[] = [
  { ms: 0, x: 760, y: 376, pose: 'idle', facing: 1 },
  { ms: 260, x: 760, y: 376, pose: 'coil', facing: 1 },
  { ms: 700, x: 760, y: 376, pose: 'coil', facing: 1 },
  { ms: 810, x: 730, y: 368, pose: 'push', facing: 1 },
  { ms: 940, x: 610, y: 340, pose: 'tuck', facing: 1 },
  { ms: 1100, x: 380, y: 353, pose: 'strike', facing: 1 },
  { ms: 1230, x: 265, y: 376, pose: 'catch', facing: 1 },
  { ms: 1360, x: 265, y: 376, pose: 'gather', facing: 1 },
  { ms: 1580, x: 155, y: 302, pose: 'reach', facing: 1 },
  { ms: 1760, x: 155, y: 276, pose: 'turn', facing: 1 },
  { ms: 1800, x: 155, y: 276, pose: 'turn', facing: -1 },
  { ms: 2030, x: 155, y: 276, pose: 'wall', facing: -1 },
  { ms: 2350, x: 155, y: 276, pose: 'wall', facing: -1 },
  { ms: 2440, x: 155, y: 276, pose: 'wallPush', facing: -1 },
  { ms: 2620, x: 405, y: 307, pose: 'tuck', facing: -1 },
  { ms: 2790, x: 615, y: 359, pose: 'strike', facing: -1 },
  { ms: 2890, x: 688, y: 376, pose: 'catch', facing: -1 },
  { ms: 3070, x: 760, y: 376, pose: 'settle', facing: -1 },
  { ms: 3340, x: 760, y: 376, pose: 'settle', facing: -1 },
  { ms: 4050, x: 760, y: 376, pose: 'idle', facing: -1 },
  { ms: HUNT_DURATION, x: 760, y: 376, pose: 'idle', facing: -1 },
]

export const HUNT_CHAPTERS = [
  { ms: 0, label: '往：獲物を狙う' },
  { ms: 700, label: '床を蹴って低く襲う' },
  { ms: 1230, label: '壁際で踏み直す' },
  { ms: 1580, label: '壁を捉え、身体を返す' },
  { ms: 2030, label: '復：壁に後脚を掛ける' },
  { ms: 2350, label: '壁を蹴り、斜め下へ襲う' },
  { ms: 2890, label: '前脚で着地、爪で制動' },
  { ms: 3340, label: '反撃の隙・復帰' },
] as const

type Foot = 'nf' | 'ff' | 'nh' | 'fh'
type Motion = { offset: { x: number; y: number }; scale: { x: number; y: number }; rot: number }
export type HuntPoseKey = {
  name: string; ms: number; duration: number; image: keyof typeof poses | 'takeoff'; facing: 1 | -1
  floor: readonly Foot[]; wall: readonly Foot[]; ease?: 'linear' | 'easeIn' | 'easeOut' | 'easeInOut' | 'step'
  slidingFloor?: readonly Foot[]
  motion?: Partial<Motion>
}
// A drawing candidate is one unique image name. Repeated rows deliberately reuse that drawing.
export const HUNT_POSE_KEYS: readonly HuntPoseKey[] = [
  { name: '狙いの四足', ms: 0, duration: 260, image: 'idle', facing: 1, floor: ['nf', 'ff', 'nh', 'fh'], wall: [] },
  { name: '胸を低く溜める', ms: 260, duration: 510, image: 'coil', facing: 1, floor: ['nf', 'ff', 'nh', 'fh'], wall: [], ease: 'easeInOut', motion: { offset: { x: 0, y: 1 }, scale: { x: 1, y: 0.99 } } },
  { name: '後脚で床を蹴る', ms: 770, duration: 170, image: 'push', facing: 1, floor: [], wall: [] },
  { name: '脚を畳み低く飛ぶ', ms: 940, duration: 160, image: 'tuck', facing: 1, floor: [], wall: [] },
  { name: '顎と片爪で襲う', ms: 1100, duration: 130, image: 'strike', facing: 1, floor: [], wall: [] },
  { name: '前爪で床を受ける', ms: 1230, duration: 130, image: 'catch', facing: 1, floor: ['nf', 'ff'], wall: [], ease: 'easeOut', motion: { scale: { x: 1.015, y: 0.97 } } },
  { name: '踏み直し、壁を見る', ms: 1360, duration: 90, image: 'coil', facing: 1, floor: ['nf', 'ff', 'nh', 'fh'], wall: [] },
  { name: '壁へ跳びつく', ms: 1450, duration: 130, image: 'push', facing: 1, floor: [], wall: [], motion: { rot: 40 } },
  { name: '前爪で壁を捉える', ms: 1580, duration: 220, image: 'reach', facing: 1, floor: [], wall: ['nf', 'ff'] },
  { name: '壁際で身体を返す', ms: 1800, duration: 230, image: 'turn', facing: -1, floor: [], wall: ['nf', 'ff'] },
  { name: '後脚で受け止める', ms: 2030, duration: 410, image: 'wall', facing: -1, floor: [], wall: ['nh', 'fh'], ease: 'easeInOut', motion: { offset: { x: 1, y: 0 }, rot: -0.35 } },
  { name: '壁を蹴り返す', ms: 2440, duration: 180, image: 'push', facing: -1, floor: [], wall: [], motion: { rot: -55 } },
  { name: '脚を畳み戻る', ms: 2620, duration: 170, image: 'tuck', facing: -1, floor: [], wall: [] },
  { name: '再び獲物を襲う', ms: 2790, duration: 100, image: 'strike', facing: -1, floor: [], wall: [] },
  { name: '前爪で着地し削る', ms: 2890, duration: 180, image: 'catch', facing: -1, floor: ['nf', 'ff'], slidingFloor: ['nf', 'ff'], wall: [] },
  { name: '後脚を下ろし停止', ms: 3070, duration: 980, image: 'settle', facing: -1, floor: ['nf', 'ff', 'nh', 'fh'], wall: [] },
  { name: '狙いの四足へ戻る', ms: 4050, duration: 350, image: 'idle', facing: -1, floor: ['nf', 'ff', 'nh', 'fh'], wall: [] },
]
export const HUNT_DRAWING_COUNT = new Set(HUNT_POSE_KEYS.map(k => k.image)).size

export type HuntDrawingPlan = 'ten' | 'seven' | 'six'
// The root trajectory is shared. Only the drawing timeline and its procedural channels vary.
const leanBase: readonly HuntPoseKey[] = [
  { name: '狙いの四足', ms: 0, duration: 260, image: 'idle', facing: 1, floor: ['nf', 'ff', 'nh', 'fh'], wall: [] },
  { name: '胸を低く溜める', ms: 260, duration: 510, image: 'coil', facing: 1, floor: ['nf', 'ff', 'nh', 'fh'], wall: [] },
  { name: '低く襲う（床蹴りから空中まで）', ms: 770, duration: 460, image: 'strike', facing: 1, floor: [], wall: [] },
  { name: '前爪で床を受け、壁を見る', ms: 1230, duration: 220, image: 'catch', facing: 1, floor: ['nf', 'ff'], wall: [] },
  { name: '壁へ跳びつく（襲う絵を傾ける）', ms: 1450, duration: 130, image: 'strike', facing: 1, floor: [], wall: [], motion: { rot: 40 } },
]
const leanEnd: readonly HuntPoseKey[] = [
  { name: '壁を蹴り返す', ms: 2440, duration: 180, image: 'strike', facing: -1, floor: [], wall: [], motion: { rot: -55 } },
  { name: '飛ぶ方向へ身体を戻して襲う', ms: 2620, duration: 270, image: 'strike', facing: -1, floor: [], wall: [], motion: { rot: 0 } },
  { name: '前爪で着地し削る', ms: 2890, duration: 180, image: 'catch', facing: -1, floor: ['nf', 'ff'], slidingFloor: ['nf', 'ff'], wall: [] },
  { name: '後脚を下ろし停止', ms: 3070, duration: 980, image: 'settle', facing: -1, floor: ['nf', 'ff', 'nh', 'fh'], wall: [] },
  { name: '狙いの四足へ戻る', ms: 4050, duration: 350, image: 'idle', facing: -1, floor: ['nf', 'ff', 'nh', 'fh'], wall: [] },
]
export const HUNT_SEVEN_KEYS: readonly HuntPoseKey[] = [
  ...leanBase,
  { name: '壁で身体を返す（前爪で捉える）', ms: 1580, duration: 450, image: 'turn', facing: -1, floor: [], wall: ['nf', 'ff'] },
  { name: '後脚で壁を受け止める', ms: 2030, duration: 410, image: 'wall', facing: -1, floor: [], wall: ['nh', 'fh'] },
  ...leanEnd,
]
export const HUNT_SIX_KEYS: readonly HuntPoseKey[] = [
  ...leanBase,
  { name: '壁を捉え、後脚で受け止める', ms: 1580, duration: 860, image: 'wall', facing: -1, floor: [], wall: ['nf', 'ff', 'nh', 'fh'] },
  ...leanEnd,
]
export const HUNT_PLANS: Record<HuntDrawingPlan, readonly HuntPoseKey[]> = {
  ten: HUNT_POSE_KEYS, seven: HUNT_SEVEN_KEYS, six: HUNT_SIX_KEYS,
}
export const huntDrawingCount = (plan: HuntDrawingPlan) => new Set(HUNT_PLANS[plan].map(key => key.image)).size

const mix = (a: number, b: number, t: number) => a + (b - a) * t
// Monotone Hermite: carries velocity between knots, never overshoots a wall or the floor.
function slope(i: number, axis: 'x' | 'y'): number {
  if (i === 0 || i === keys.length - 1) return 0
  const a = keys[i - 1], b = keys[i], c = keys[i + 1]
  const l = (b[axis] - a[axis]) / (b.ms - a.ms)
  const r = (c[axis] - b[axis]) / (c.ms - b.ms)
  return l * r <= 0 ? 0 : 2 * l * r / (l + r)
}
function position(i: number, t: number, axis: 'x' | 'y', height: HuntWallHeight): number {
  const a = keys[i], b = keys[i + 1], dt = b.ms - a.ms
  // Free flight has no Hermite landing tangent: horizontal travel stays uniform,
  // while gravity keeps increasing downward velocity right up to contact.
  const ms = mix(a.ms, b.ms, t)
  const lift = HUNT_HEIGHT_LIFT[height]
  const flight = ms >= 810 && ms <= 1230 ? { start: 810, end: 1230, x0: 730, x1: 265, y0: 368, y1: 376, vy: -0.15 }
    : ms >= 2440 && ms <= 2890 ? { start: 2440, end: 2890, x0: 155, x1: 688, y0: 276 - lift, y1: 376, vy: -0.035 }
      : undefined
  if (flight) {
    const elapsed = ms - flight.start, duration = flight.end - flight.start
    return axis === 'x' ? mix(flight.x0, flight.x1, elapsed / duration)
      : flight.y0 + flight.vy * elapsed + (flight.y1 - flight.y0 - flight.vy * duration) * (elapsed / duration) ** 2
  }
  if (axis === 'x' && ms >= 700 && ms <= 770) return 760
  if (axis === 'x' && ms > 770 && ms < 810) {
    // Accelerate to the following flight speed, then hold it; no launch overshoot.
    const flightSpeed = 465 / 420, ramp = 2 * (40 - 30 / flightSpeed), elapsed = ms - 770
    return 760 - (elapsed < ramp ? flightSpeed * elapsed ** 2 / (2 * ramp) : flightSpeed * (elapsed - ramp / 2))
  }
  if (ms >= 1360 && ms <= 1450) return axis === 'x' ? 265 : 376
  if (ms > 1450 && ms < 1580) {
    const elapsed = ms - 1450, duration = 130
    // Wall approach is another flight: retain speed through first claw contact.
    return axis === 'x' ? 265 - 110 * elapsed / duration
      : 376 - (0.85 + lift / duration) * elapsed + (-74 + 0.85 * duration) * (elapsed / duration) ** 2
  }
  // 3乗は exactCube (正しく丸めた値)。 `t ** 3` は Node/V8 の作りで最後の1ビットが違い、往復の記録の sha256 が環境で変わった (src/exact_pow.ts)。
  const t3 = exactCube(t)
  const value = (2 * t3 - 3 * t * t + 1) * a[axis]
    + (t3 - 2 * t * t + t) * dt * slope(i, axis)
    + (-2 * t3 + 3 * t * t) * b[axis]
    + (t3 - t * t) * dt * slope(i + 1, axis)
  if (axis !== 'y' || !lift) return value
  // Contact height grows during the wall approach, then stays fixed until release.
  const rise = (k: Key) => k.ms >= 1580 && k.ms <= 2440 ? lift : 0
  return value - mix(rise(a), rise(b), t * t * (3 - 2 * t))
}
export type HuntFrame = {
  ms: number; x: number; y: number; facing: 1 | -1; pose: HuntPose; chapter: string
  motion?: Motion; key?: HuntPoseKey; pathSize?: HuntPathSize; proxyScale?: number; sourceFrame?: HuntFrame; takeoff?: HuntTakeoffMode
}
export function huntRealArtBounds(frame: HuntFrame, placement: HuntRealPlacement, art: ArtSource): readonly [number, number, number, number] {
  const points = huntArtHull(art).map(point => huntRealArtPoint(frame, placement, point))
  return [Math.min(...points.map(point => point[0])), Math.min(...points.map(point => point[1])),
    Math.max(...points.map(point => point[0])), Math.max(...points.map(point => point[1]))]
}
export type HuntProxyPlacement = { x: number; y: number; scale: number; rotation: number }
export function huntProxyBounds(frame: HuntFrame, at: HuntProxyPlacement, outline: readonly (readonly [number, number])[]): readonly [number, number, number, number] {
  const sx = at.scale * (frame.motion?.scale.x ?? 1), sy = at.scale * (frame.motion?.scale.y ?? 1)
  const cos = Math.cos(at.rotation), sin = Math.sin(at.rotation)
  let left = Infinity, top = Infinity, right = -Infinity, bottom = -Infinity
  for (const [px, py] of outline) {
    const dx = (px - 480) * sx, dy = (py - 520) * sy
    const x = at.x + (dx * cos - dy * sin) * frame.facing
    const y = at.y + dx * sin + dy * cos
    left = Math.min(left, x); right = Math.max(right, x)
    top = Math.min(top, y); bottom = Math.max(bottom, y)
  }
  return [left, top, right, bottom]
}
// The lab supplies the actual alpha outline of its procedurally drawn proxy.
export function huntProxyPlacement(frame: HuntFrame, side: WallSide, outline: readonly (readonly [number, number])[]): HuntProxyPlacement {
  const at: HuntProxyPlacement = { x: frame.x + (frame.motion?.offset.x ?? 0), y: frame.y + (frame.motion?.offset.y ?? 0),
    scale: frame.proxyScale ?? HUNT_SCALE, rotation: (frame.motion?.rot ?? 0) * Math.PI / 180 }
  if (frame.pathSize !== 'real' || !outline.length) return at
  const wall = frame.key?.wall.length ? side : undefined
  const floor = !!frame.key?.floor.length
  for (let i = 0; i < 3; i++) {
    let [left, top, right, bottom] = huntProxyBounds(frame, at, outline)
    if (wall === 'left') at.x += HUNT_WALLS.left - left
    if (wall === 'right') at.x += HUNT_WALLS.right - right
    if (floor) at.y += HUNT_FLOOR - bottom
    ;[left, top, right, bottom] = huntProxyBounds(frame, at, outline)
    const factor = Math.min(1, (HUNT_WALLS.right - HUNT_WALLS.left) / (right - left), (HUNT_FLOOR - 8) / (bottom - top))
    if (factor < 1) {
      at.scale *= factor
      continue
    }
    if (!wall) at.x += Math.max(0, HUNT_WALLS.left - left) - Math.max(0, right - HUNT_WALLS.right)
    if (!floor) at.y += Math.max(0, 8 - top) - Math.max(0, bottom - HUNT_FLOOR)
    ;[left, top, right, bottom] = huntProxyBounds(frame, at, outline)
    if (wall === 'left') at.x += HUNT_WALLS.left - left
    if (wall === 'right') at.x += HUNT_WALLS.right - right
    if (floor) at.y += HUNT_FLOOR - bottom
    if (!wall && !floor) break
  }
  const [left, top, right, bottom] = huntProxyBounds(frame, at, outline)
  if (!wall) at.x += Math.max(0, HUNT_WALLS.left - left) - Math.max(0, right - HUNT_WALLS.right)
  if (!floor) at.y += Math.max(0, 8 - top) - Math.max(0, bottom - HUNT_FLOOR)
  return at
}
// The playable strip is only 840px wide. Expand distance from the target near
// centre, but taper continuously to the unchanged wall planes at 60 and 900.
export function huntRealPathX(x: number): number {
  const distance = Math.abs(x - 480) / 420
  return 480 + Math.sign(x - 480) * 420 * Math.min(1, exactPow08(distance))
}
export function huntRealPathY(y: number): number { return 440 - (440 - y) * 1.28 }
function huntRealFlightX(frame: HuntFrame): number {
  if (frame.takeoff === 'A' && frame.ms >= 770 && frame.ms < 1580) return huntRealPathX(frame.x)
  const flights = [[810, 1230, 730, 265], [1450, 1580, 265, 155], [2440, 2890, 155, 688]] as const
  const flight = flights.find(([start, end]) => frame.ms >= start && frame.ms <= end)
  if (!flight) return huntRealPathX(frame.x)
  const [start, end, from, to] = flight
  const progress = (frame.ms - start) / (end - start)
  const left = mix(huntRealPathX(from), huntRealPathX(to), progress)
  const oldLeft = mix(from, to, progress)
  // Original frames are mirrored around the room centre for the right wall.
  return Math.abs(frame.x - oldLeft) <= Math.abs(frame.x - (960 - oldLeft)) ? left : 960 - left
}
export function huntRealSizeFrame(frame: HuntFrame): HuntFrame {
  const x = huntRealFlightX(frame), y = huntRealPathY(frame.y)
  const motion = frame.motion && { ...frame.motion, offset: {
    x: huntRealPathX(frame.x + frame.motion.offset.x) - x,
    y: huntRealPathY(frame.y + frame.motion.offset.y) - y,
  } }
  return { ...frame, x, y, motion, pathSize: 'real', proxyScale: HUNT_REAL_PROXY_SCALE, sourceFrame: frame }
}
export function sampleWallHuntSized(timeMs: number, side: WallSide = 'left', height: HuntWallHeight = 'current', life: HuntWallLife = 'none', size: HuntPathSize = 'real', takeoff: HuntTakeoffMode = 'B'): HuntFrame {
  const frame = takeoff === 'A' ? huntTakeoffFrame(sampleWallHunt(timeMs, side, height, life), side) : sampleWallHunt(timeMs, side, height, life)
  return size === 'real' ? huntRealSizeFrame(frame) : frame
}
export function sampleWallHuntSteppedSized(timeMs: number, side: WallSide = 'left', plan: HuntDrawingPlan = 'six', height: HuntWallHeight = 'high', life: HuntWallLife = 'weak', size: HuntPathSize = 'real', takeoff: HuntTakeoffMode = 'B'): HuntFrame {
  const frame = takeoff === 'A' && plan === 'six' ? huntTakeoffFrame(sampleWallHuntStepped(timeMs, side, plan, height, life), side) : sampleWallHuntStepped(timeMs, side, plan, height, life)
  return size === 'real' ? huntRealSizeFrame(frame) : frame
}
export const HUNT_TAKEOFF_KEYS: readonly HuntPoseKey[] = HUNT_SIX_KEYS.map(key => key.ms === 1230
  ? { ...key, name: '後脚で踏み切る', image: 'takeoff', floor: ['nh', 'fh'], slidingFloor: ['nh', 'fh'] }
  : key)
function huntTakeoffFrame(frame: HuntFrame, side: WallSide): HuntFrame {
  const ms = frame.ms
  if (ms < 770 || ms >= 1580) return frame
  // Keep the 1.23s contact moving horizontally into the unchanged wall arrival.
  const leftX = ms < 1230 ? 760 - 295 * (ms - 770) / 460 : 465 - 310 * (ms - 1230) / 350
  const y = ms < 1230 ? frame.y : 376 - 74 * (ms - 1230) / 350
  const key = frame.key?.ms === 1230 ? HUNT_TAKEOFF_KEYS.find(k => k.ms === 1230) : frame.key
  return { ...frame, x: side === 'left' ? leftX : 960 - leftX, y, key, takeoff: 'A',
    motion: frame.key?.ms === 1230 ? { offset: { x: 0, y: 0 }, scale: { x: 1, y: 1 }, rot: 0 } : frame.motion }
}
export function huntWorldPoint(frame: HuntFrame, key: Foot | 'shoulder' | 'hip'): [number, number] {
  if (frame.sourceFrame) {
    const [x, y] = huntWorldPoint(frame.sourceFrame, key)
    return [huntRealPathX(x), huntRealPathY(y)]
  }
  const foot = frame.pose[key]
  const far = key === 'ff' || key === 'fh' ? 18 : 0
  const m = frame.motion
  const dx = (foot[0] + far - 480) * HUNT_SCALE * (m?.scale.x ?? 1)
  const dy = (foot[1] - 520) * HUNT_SCALE * (m?.scale.y ?? 1)
  const rot = (m?.rot ?? 0) * Math.PI / 180
  return [frame.x + (m?.offset.x ?? 0) + (dx * Math.cos(rot) - dy * Math.sin(rot)) * frame.facing,
    frame.y + (m?.offset.y ?? 0) + dx * Math.sin(rot) + dy * Math.cos(rot)]
}
export function sampleWallHunt(timeMs: number, side: WallSide = 'left', height: HuntWallHeight = 'current', life: HuntWallLife = 'none'): HuntFrame {
  const ms = Math.max(0, Math.min(HUNT_DURATION, Number.isFinite(timeMs) ? timeMs : 0))
  const i = Math.max(0, keys.findIndex(k => k.ms >= ms) - 1)
  const a = keys[i], b = keys[i + 1]
  const t = (ms - a.ms) / (b.ms - a.ms), u = t * t * (3 - 2 * t)
  const pa = poses[a.pose], pb = poses[b.pose]
  const p = {} as HuntPose
  for (const key of ['shoulder', 'hip', 'head', 'nf', 'ff', 'nh', 'fh'] as const) {
    p[key] = [mix(pa[key][0], pb[key][0], u), mix(pa[key][1], pb[key][1], u)]
  }
  for (const key of ['jaw', 'headAngle', 'arch', 'frontBend', 'hindBend'] as const) {
    p[key] = mix(pa[key] ?? 0, pb[key] ?? 0, u)
  }
  // Joint rotation, not shrinking the torso between crouch and release.
  const angle = Math.atan2(p.hip[1] - p.shoulder[1], p.hip[0] - p.shoulder[0])
  p.hip = [p.shoulder[0] + Math.cos(angle) * 290, p.shoulder[1] + Math.sin(angle) * 290]
  const rawFacing = ms < 1800 ? 1 : -1
  const x = position(i, t, 'x', height), y = position(i, t, 'y', height)
  const frame: HuntFrame = { ms, x: side === 'left' ? x : 960 - x, y,
    facing: (side === 'left' ? rawFacing : -rawFacing) as 1 | -1, pose: p,
    chapter: [...HUNT_CHAPTERS].reverse().find(c => ms >= c.ms)!.label }
  const wall = HUNT_WALLS[side]
  const lift = HUNT_HEIGHT_LIFT[height]
  const pin = (key: 'nf' | 'ff' | 'nh' | 'fh', wx: number, wy: number) => {
    const far = key === 'ff' || key === 'fh' ? 18 : 0
    p[key] = [480 + (wx - frame.x) / (HUNT_SCALE * frame.facing) - far,
      520 + (wy - frame.y) / HUNT_SCALE]
  }
  // Front claws hold the wall during the pivot; hind feet take over for the rebound.
  // 1580/2030/2440 key points use exactly these contacts, so release has no jump.
  if (ms >= 1580 && ms <= 2030) {
    pin('nf', wall, 273.2 - lift); pin('ff', wall, 300.4 - lift)
  }
  if (ms >= 1800 && ms < 2030) {
    // Keep the free feet in world space across the hard facing change.
    const u = (ms - 1800) / 230
    for (const key of ['nh', 'fh'] as const) {
      const far = key === 'fh' ? 18 : 0
      const fromX = 155 + (poses.turn[key][0] + far - 480) * HUNT_SCALE
      const fromY = 276 + (poses.turn[key][1] - 520) * HUNT_SCALE - lift
      pin(key, mix(side === 'left' ? fromX : 960 - fromX, wall, u), mix(fromY, (key === 'nh' ? 314.4 : 343.2) - lift, u))
    }
  }
  if (ms >= 2030 && ms <= 2440) {
    pin('nh', wall, 314.4 - lift); pin('fh', wall, 343.2 - lift)
  }
  if (ms > 2030 && ms <= 2440) {
    pin('nf', wall, 273.2 - lift); pin('ff', wall, 300.4 - lift)
  }
  if (ms > 2440 && ms < 2620) {
    const u = (ms - 2440) / 180
    for (const [key, wallY] of [['nf', 273.2], ['ff', 300.4], ['nh', 314.4], ['fh', 343.2]] as const) {
      const target = huntWorldPoint(frame, key)
      pin(key, mix(wall, target[0], u), mix(wallY - lift, target[1], u))
    }
  }
  // Feet remain planted while loading the first leap.
  if (ms <= 770) {
    for (const key of ['nf', 'ff', 'nh', 'fh'] as const) {
      const far = key === 'ff' || key === 'fh' ? 18 : 0
      const footX = 760 + (poses.coil[key][0] + far - 480) * HUNT_SCALE
      pin(key, side === 'left' ? footX : 960 - footX, HUNT_FLOOR)
    }
  }
  if (ms >= 1230 && ms <= 1360) {
    for (const key of ['nf', 'ff'] as const) {
      const far = key === 'ff' ? 18 : 0
      const leftX = 265 + (poses.catch[key][0] + far - 480) * HUNT_SCALE
      pin(key, side === 'left' ? leftX : 960 - leftX, HUNT_FLOOR)
    }
  }
  if (ms > 1360 && ms <= 1450) {
    for (const key of ['nf', 'ff', 'nh', 'fh'] as const) {
      const far = key === 'ff' || key === 'fh' ? 18 : 0
      const leftX = key === 'nf' || key === 'ff'
        ? 265 + (poses.catch[key][0] + far - 480) * HUNT_SCALE
        : 265 + (poses.gather[key][0] + far - 480) * HUNT_SCALE
      pin(key, side === 'left' ? leftX : 960 - leftX, HUNT_FLOOR)
    }
  }
  if (ms > 1450 && ms < 1580) {
    const u = (ms - 1450) / 130
    for (const key of ['nf', 'ff', 'nh', 'fh'] as const) {
      const far = key === 'ff' || key === 'fh' ? 18 : 0
      const leftX = key === 'nf' || key === 'ff'
        ? 265 + (poses.catch[key][0] + far - 480) * HUNT_SCALE
        : 265 + (poses.gather[key][0] + far - 480) * HUNT_SCALE
      const target = key === 'nf' ? [wall, 273.2 - lift] : key === 'ff' ? [wall, 300.4 - lift] : huntWorldPoint(frame, key)
      pin(key, mix(side === 'left' ? leftX : 960 - leftX, target[0], u), mix(HUNT_FLOOR, target[1], u))
    }
  }
  if (ms >= 3070) {
    for (const key of ['nf', 'ff', 'nh', 'fh'] as const) {
      const far = key === 'ff' || key === 'fh' ? 18 : 0
      const leftX = 760 - (poses.settle[key][0] + far - 480) * HUNT_SCALE
      pin(key, side === 'left' ? leftX : 960 - leftX, HUNT_FLOOR)
    }
  }
  if (life !== 'none' && ms >= 1580 && ms < 2440) {
    const phase = ms < 2030 ? (ms - 1580) / 450 : (ms - 2030) / 410
    const duration = ms < 2030 ? 450 : 410
    const foot = ms < 2030 ? 'nf' : 'nh'
    const [, baselineY] = huntWorldPoint(frame, foot)
    const motion = wallLifeMotion(phase, duration, life)
    frame.motion = motion
    const [footX, footY] = huntWorldPoint(frame, foot)
    motion.offset.x += wall - footX
    motion.offset.y += baselineY - footY
  }
  return frame
}

function eased(t: number, ease: HuntPoseKey['ease']): number {
  switch (ease) {
    case 'step': return 0
    case 'easeIn': return t * t
    case 'easeOut': return 1 - (1 - t) ** 2
    case 'easeInOut': return t * t * (3 - 2 * t)
    default: return t
  }
}
const emptyMotion: Motion = { offset: { x: 0, y: 0 }, scale: { x: 1, y: 1 }, rot: 0 }
function wallLifeMotion(phase: number, _duration: number, life: HuntWallLife): Motion {
  const amount = life === 'weak' ? 1 : 2
  const envelope = Math.sin(Math.PI * phase) ** 2
  const catchLoad = Math.sin(Math.PI * Math.min(1, phase * 5)) ** 2 * (1 - phase)
  const kickLoad = phase > 0.76 ? Math.sin(Math.PI * (phase - 0.76) / 0.24) ** 2 : 0
  const breath = Math.sin(phase * Math.PI * 3) * envelope
  // Rotation and compression are applied about the planted wall foot below.
  // The first and last pulses are the impact and the load before the kick;
  // the slower middle pulse reads as breathing between them.
  return { offset: { x: 0, y: 0 },
    scale: { x: 1, y: 1 + amount * (-0.035 * catchLoad + 0.03 * breath - 0.04 * kickLoad) },
    rot: amount * (1.6 * catchLoad + 1.55 * breath + 2.3 * kickLoad) }
}
// A drawing has one pose regardless of which plan uses it or when it appears.
// These reference times preserve the existing ten-drawing comparison exactly.
const drawingReference: Record<keyof typeof poses, number> = {
  idle: 0, coil: 260, push: 770, tuck: 940, strike: 1100, catch: 1230,
  gather: 1360, reach: 1580, turn: 1800, wall: 2030, wallPush: 2440, settle: 3070,
}
const drawingPose = new Map<keyof typeof poses, HuntPose>(
  (Object.keys(drawingReference) as (keyof typeof poses)[]).map(image => [image, sampleWallHunt(drawingReference[image], 'left').pose]),
)
export function sampleWallHuntStepped(timeMs: number, side: WallSide = 'left', plan: HuntDrawingPlan = 'ten', height: HuntWallHeight = 'current', life: HuntWallLife = 'none'): HuntFrame {
  const root = sampleWallHunt(timeMs, side, height)
  const planKeys = HUNT_PLANS[plan]
  const nextIndex = planKeys.findIndex(k => k.ms > root.ms)
  const index = nextIndex < 0 ? planKeys.length - 1 : Math.max(0, nextIndex - 1)
  const key = planKeys[index]
  const next = planKeys[index + 1]
  const progress = next ? eased((root.ms - key.ms) / key.duration, key.ease) : 0
  const a = key.motion, b = next?.motion
  const channel = (from: number | undefined, to: number | undefined, fallback: number) => mix(from ?? fallback, to ?? fallback, progress)
  const start = sampleWallHunt(key.ms, side, height)
  const planted = key.floor.some(foot => !key.slidingFloor?.includes(foot)) || key.wall.length > 0
  const motion: Motion = {
    offset: {
      x: channel(a?.offset?.x, b?.offset?.x, 0) * (side === 'left' ? 1 : -1) + (planted ? start.x - root.x : 0),
      y: channel(a?.offset?.y, b?.offset?.y, 0) + (planted ? start.y - root.y : 0),
    },
    scale: { x: channel(a?.scale?.x, b?.scale?.x, 1), y: channel(a?.scale?.y, b?.scale?.y, 1) },
    // A contact pose cannot rotate toward the next airborne key while its feet are planted.
    rot: planted ? (a?.rot ?? 0) : a?.rot ?? channel(a?.rot, b?.rot, 0),
  }
  const p = drawingPose.get(key.image === 'takeoff' ? 'catch' : key.image)!
  const frame: HuntFrame = { ...root, pose: p, facing: side === 'left' ? key.facing : key.facing === 1 ? -1 : 1,
    motion: { ...emptyMotion, ...motion }, key }
  if (plan !== 'ten' || (life !== 'none' && key.image === 'wall')) {
    // Fit the immutable drawing to the declared contact. The root remains untouched.
    if (key.wall.length) {
      const [x] = huntWorldPoint(frame, key.wall[0])
      frame.motion!.offset.x += HUNT_WALLS[side] - x
    }
    if (key.floor.length && !key.slidingFloor?.includes(key.floor[0])) {
      const [, y] = huntWorldPoint(frame, key.floor[0])
      frame.motion!.offset.y += HUNT_FLOOR - y
    }
  }
  if (life !== 'none' && key.image === 'wall' && key.wall.length) {
    const planted = key.wall.map(foot => huntWorldPoint(frame, foot))
    const pulse = wallLifeMotion((root.ms - key.ms) / key.duration, key.duration, life)
    frame.motion!.scale.y += pulse.scale.y - 1
    frame.motion!.rot += pulse.rot
    const moved = key.wall.map(foot => huntWorldPoint(frame, foot))
    frame.motion!.offset.x += planted.reduce((sum, point, i) => sum + point[0] - moved[i][0], 0) / planted.length
    frame.motion!.offset.y += planted.reduce((sum, point, i) => sum + point[1] - moved[i][1], 0) / planted.length
  }
  return frame
}
